import React, { useState, useEffect } from 'react';
import TextInput from '../../common/TextInput';
import { dictionaryEntryApi } from '../../../services/apis/configuredApis';
import { canonicalTermApi } from '../../../services/apis/configuredApis';
import './DictionaryModal.css';

function DictionaryEntryModal({ show, onClose, onSave, entry, graphId }) {
  const [formData, setFormData] = useState({
    scope_type: 'GRAPH',
    graph_id: graphId || '',
    canonical_term_id: '',
    synonym: '',
    synonym_norm: '',
    is_pattern: false,
    override_global: false,
    is_enabled: true,
    weight: 0,
    metadata: {}
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [canonicalTerms, setCanonicalTerms] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);

  useEffect(() => {
    if (show) {
      // Fetch canonical terms when modal opens
      fetchCanonicalTerms();
    }
  }, [show]);

  useEffect(() => {
    if (entry) {
      setFormData({
        scope_type: entry.scope_type || 'GRAPH',
        graph_id: entry.graph_id || graphId || '',
        canonical_term_id: entry.canonical_term_id || '',
        synonym: entry.synonym || '',
        synonym_norm: entry.synonym_norm || '',
        is_pattern: entry.is_pattern || false,
        override_global: entry.override_global || false,
        is_enabled: entry.is_enabled !== undefined ? entry.is_enabled : true,
        weight: entry.weight || 0,
        metadata: entry.metadata || entry.meta_data || {}
      });
    } else {
      setFormData({
        scope_type: graphId ? 'GRAPH' : 'GLOBAL',
        graph_id: graphId || '',
        canonical_term_id: '',
        synonym: '',
        synonym_norm: '',
        is_pattern: false,
        override_global: false,
        is_enabled: true,
        weight: 0,
        metadata: {}
      });
    }
    setErrors({});
    setApiError(null);
  }, [entry, show, graphId]);

  const fetchCanonicalTerms = async () => {
    setLoadingTerms(true);
    try {
      const response = await canonicalTermApi.canonicalTermListGet(null, 100, 0);
      const responseData = response?.data || response;
      const termsList = Array.isArray(responseData) ? responseData : (responseData?.terms || []);
      setCanonicalTerms(termsList);
    } catch (err) {
      console.error('Error fetching canonical terms:', err);
      setCanonicalTerms([]);
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Auto-generate normalized synonym (uppercase, trimmed)
    if (field === 'synonym' && value) {
      const normalized = value.trim().toUpperCase();
      setFormData(prev => ({ ...prev, synonym_norm: normalized }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.canonical_term_id) {
      newErrors.canonical_term_id = 'Canonical term is required';
    }
    if (!formData.synonym.trim()) {
      newErrors.synonym = 'Synonym is required';
    }
    if (formData.scope_type === 'GRAPH' && !formData.graph_id) {
      newErrors.graph_id = 'Graph ID is required for graph scope';
    }
    if (formData.scope_type === 'GLOBAL' && formData.graph_id) {
      newErrors.graph_id = 'Graph ID must be empty for global scope';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    setApiError(null);

    try {
      // Prepare API payload
      const apiPayload = {
        scope_type: formData.scope_type,
        canonical_term_id: formData.canonical_term_id,
        synonym: formData.synonym.trim(),
        synonym_norm: formData.synonym_norm.trim() || formData.synonym.trim().toUpperCase(),
        graph_id: formData.scope_type === 'GRAPH' ? (formData.graph_id || graphId) : null,
        is_pattern: formData.is_pattern,
        override_global: formData.override_global,
        is_enabled: formData.is_enabled,
        weight: formData.weight || 0,
        meta_data: formData.metadata || {}
      };

      if (entry && entry.id) {
        // Update existing entry
        await dictionaryEntryApi.dictionaryEntryUpdateEntryIdPut(entry.id, apiPayload);
      } else {
        // Create new entry
        await dictionaryEntryApi.dictionaryEntryCreatePost(apiPayload);
      }

      // Call onSave callback to notify parent component
      onSave(formData);
    } catch (err) {
      console.error('Error saving dictionary entry:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to save dictionary entry. Please try again.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="dictionary-modal-overlay" onClick={onClose}>
      <div className="dictionary-modal dictionary-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="dictionary-modal-header">
          <h2>{entry ? 'Edit Dictionary Entry' : 'Add Dictionary Entry'}</h2>
          <button className="dictionary-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="dictionary-modal-body">
            <div className="dictionary-form-group">
              <label>Canonical Term *</label>
              <select
                value={formData.canonical_term_id}
                onChange={(e) => handleChange('canonical_term_id', e.target.value)}
                className={errors.canonical_term_id ? 'error' : ''}
                required
                disabled={loadingTerms}
              >
                <option value="">{loadingTerms ? 'Loading...' : 'Select a canonical term'}</option>
                {canonicalTerms.map(term => (
                  <option key={term.id} value={term.id}>
                    {term.code} - {term.display}
                  </option>
                ))}
              </select>
              {errors.canonical_term_id && (
                <div className="dictionary-error">{errors.canonical_term_id}</div>
              )}
            </div>

            <TextInput
              label="Synonym *"
              variant="modal"
              value={formData.synonym}
              onChange={(e) => handleChange('synonym', e.target.value)}
              placeholder="Enter synonym"
              required
              className={errors.synonym ? 'error' : ''}
            />
            {errors.synonym && <div className="dictionary-error">{errors.synonym}</div>}

            {apiError && (
              <div className="dictionary-error" style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                {apiError}
              </div>
            )}

            <TextInput
              label="Normalized Synonym"
              variant="modal"
              value={formData.synonym_norm}
              onChange={(e) => handleChange('synonym_norm', e.target.value)}
              placeholder="Auto-generated from synonym"
              disabled
            />

            <div className="dictionary-form-group">
              <label>Scope Type *</label>
              <select
                value={formData.scope_type}
                onChange={(e) => handleChange('scope_type', e.target.value)}
                required
              >
                <option value="GLOBAL">Global</option>
                <option value="GRAPH">Graph</option>
              </select>
            </div>

            {formData.scope_type === 'GRAPH' && (
              <TextInput
                label="Graph ID *"
                variant="modal"
                value={formData.graph_id}
                onChange={(e) => handleChange('graph_id', e.target.value)}
                placeholder="Enter graph ID"
                required
                className={errors.graph_id ? 'error' : ''}
              />
            )}
            {errors.graph_id && <div className="dictionary-error">{errors.graph_id}</div>}

            <TextInput
              label="Weight"
              variant="modal"
              type="number"
              step="0.1"
              value={formData.weight}
              onChange={(e) => handleChange('weight', parseFloat(e.target.value) || 0)}
              placeholder="0"
            />

            <div className="dictionary-form-group dictionary-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_pattern}
                  onChange={(e) => handleChange('is_pattern', e.target.checked)}
                />
                <span>Is Pattern</span>
              </label>
            </div>

            <div className="dictionary-form-group dictionary-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.override_global}
                  onChange={(e) => handleChange('override_global', e.target.checked)}
                />
                <span>Override Global</span>
              </label>
            </div>

            <div className="dictionary-form-group dictionary-form-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_enabled}
                  onChange={(e) => handleChange('is_enabled', e.target.checked)}
                />
                <span>Enabled</span>
              </label>
            </div>
          </div>
          <div className="dictionary-modal-footer">
            <button
              type="button"
              className="dictionary-btn dictionary-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="dictionary-btn dictionary-btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (entry ? 'Update' : 'Create') + ' Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DictionaryEntryModal;

